import { useEffect } from "react";
import "../planning-touch-drag.css";
import { edgeScrollDelta, pointerMovedEnough } from "../lib/planningPointerDrag";

type ActivePointerDrag = {
  pointerId: number;
  startX: number;
  startY: number;
  card: HTMLElement;
  dragging: boolean;
  targetColumn: HTMLElement | null;
};

const CARD_SELECTOR = ".planning-dnd-card";
const COLUMN_SELECTOR = ".planning-dnd-column";
const SELECT_BUTTON_SELECTOR = ".planning-dnd-card__select";
const DROP_BUTTON_SELECTOR = ".planning-dnd-drop-action";
const INTERACTIVE_SELECTOR = "button, input, select, textarea, a, [role='button']";

export function PlanningPointerDragBridge() {
  useEffect(() => {
    const panel = document.querySelector<HTMLElement>(".planning-dnd-panel");
    if (!panel) return;
    const root = panel;

    let active: ActivePointerDrag | null = null;

    function clearTarget() {
      active?.targetColumn?.classList.remove("planning-dnd-column--pointer-target");
      if (active) active.targetColumn = null;
    }

    function cleanup() {
      if (!active) return;
      clearTarget();
      active.card.classList.remove("planning-dnd-card--pointer-dragging");
      if (active.card.hasPointerCapture?.(active.pointerId)) {
        active.card.releasePointerCapture(active.pointerId);
      }
      active = null;
    }

    function updateTarget(clientX: number, clientY: number) {
      if (!active) return;
      const element = document.elementFromPoint(clientX, clientY);
      const next = element?.closest<HTMLElement>(COLUMN_SELECTOR) ?? null;
      const valid = next && root.contains(next) ? next : null;
      if (valid === active.targetColumn) return;
      clearTarget();
      active.targetColumn = valid;
      valid?.classList.add("planning-dnd-column--pointer-target");
    }

    function autoScroll(clientX: number) {
      const board = root.querySelector<HTMLElement>(".planning-dnd-board");
      if (!board) return;
      const rect = board.getBoundingClientRect();
      const delta = edgeScrollDelta(clientX, rect.left, rect.right);
      if (delta !== 0) board.scrollLeft += delta;
    }

    function onPointerDown(event: PointerEvent) {
      if (event.pointerType === "mouse" || event.button !== 0 || active) return;
      if (!(event.target instanceof Element)) return;
      if (event.target.closest(INTERACTIVE_SELECTOR)) return;

      const card = event.target.closest<HTMLElement>(CARD_SELECTOR);
      if (!card || !root.contains(card)) return;
      const selectButton = card.querySelector<HTMLButtonElement>(SELECT_BUTTON_SELECTOR);
      if (!selectButton || selectButton.disabled) return;

      if (selectButton.getAttribute("aria-pressed") !== "true") selectButton.click();
      active = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        card,
        dragging: false,
        targetColumn: null,
      };
      card.setPointerCapture?.(event.pointerId);
    }

    function onPointerMove(event: PointerEvent) {
      if (!active || event.pointerId !== active.pointerId) return;
      if (
        !active.dragging &&
        !pointerMovedEnough(active.startX, active.startY, event.clientX, event.clientY)
      ) return;

      active.dragging = true;
      active.card.classList.add("planning-dnd-card--pointer-dragging");
      event.preventDefault();
      updateTarget(event.clientX, event.clientY);
      autoScroll(event.clientX);
    }

    function onPointerUp(event: PointerEvent) {
      if (!active || event.pointerId !== active.pointerId) return;
      const target = active.targetColumn;
      const shouldDrop = active.dragging && target !== null;
      cleanup();
      if (!shouldDrop || !target) return;
      const dropButton = target.querySelector<HTMLButtonElement>(DROP_BUTTON_SELECTOR);
      if (dropButton && !dropButton.disabled) dropButton.click();
    }

    function onPointerCancel(event: PointerEvent) {
      if (active && event.pointerId === active.pointerId) cleanup();
    }

    root.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("pointermove", onPointerMove, { passive: false });
    document.addEventListener("pointerup", onPointerUp);
    document.addEventListener("pointercancel", onPointerCancel);

    return () => {
      cleanup();
      root.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
      document.removeEventListener("pointercancel", onPointerCancel);
    };
  }, []);

  return null;
}
